import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Lock,
  Unlock,
  Users,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  CreditCard,
  Eye,
  LogIn,
  ArrowRight,
  Upload,
  Image,
  LayoutDashboard,
  FileText,
  Calculator,
  ShoppingCart,
  Kanban,
  ClipboardList,
  Target,
  UsersRound,
  BarChart3,
  UserCog,
  Key,
  Landmark,
  Settings,
  ChevronDown,
  ChevronRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLANOS = [
  { value: "BASICO", label: "Básico", usuarios: 5, valor: 99.90 },
  { value: "PROFISSIONAL", label: "Profissional", usuarios: 15, valor: 199.90 },
  { value: "ENTERPRISE", label: "Enterprise", usuarios: 50, valor: 399.90 }
];

// Mapeamento de ícones para os módulos
const ICONE_MAP = {
  LayoutDashboard: LayoutDashboard,
  Users: Users,
  Landmark: Landmark,
  FileText: FileText,
  Calculator: Calculator,
  Building2: Building2,
  ShoppingCart: ShoppingCart,
  DollarSign: DollarSign,
  Kanban: Kanban,
  ClipboardList: ClipboardList,
  Target: Target,
  UsersRound: UsersRound,
  BarChart3: BarChart3,
  UserCog: UserCog,
  Key: Key,
  Settings: Settings
};

export default function GestaoEmpresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroPlano, setFiltroPlano] = useState("todos");
  
  // Empresa atual (contexto)
  const [empresaAtual, setEmpresaAtual] = useState(null);
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("dados");
  
  // Módulos do sistema
  const [modulosSistema, setModulosSistema] = useState([]);
  
  // Logo upload
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Estado para módulos expandidos
  const [modulosExpandidos, setModulosExpandidos] = useState({});
  
  // Form
  const [formData, setFormData] = useState({
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    email: "",
    telefone: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    plano: "BASICO",
    status: "ATIVA",
    data_vencimento: "",
    observacoes: "",
    logo_url: "",
    permissoes_empresa: []  // Agora usa permissões detalhadas
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    ativas: 0,
    bloqueadas: 0,
    inadimplentes: 0
  });

  useEffect(() => {
    carregarEmpresas();
    carregarModulos();
    // Carregar empresa atual do localStorage
    const empresa = JSON.parse(localStorage.getItem("empresa") || "null");
    setEmpresaAtual(empresa);
  }, []);

  const carregarModulos = async () => {
    try {
      const response = await axios.get(`${API}/api/modulos-com-permissoes`);
      setModulosSistema(response.data.modulos || []);
    } catch (error) {
      console.error("Erro ao carregar módulos:", error);
    }
  };

  // Função para acessar o ambiente de uma empresa
  const acessarEmpresa = (empresa) => {
    if (empresa.status !== "ATIVA") {
      toast.error("Esta empresa está bloqueada ou inativa");
      return;
    }
    
    // Salvar empresa no contexto
    const empresaContexto = {
      id: empresa.id,
      razao_social: empresa.razao_social,
      nome_fantasia: empresa.nome_fantasia,
      cnpj: empresa.cnpj,
      plano: empresa.plano,
      limite_usuarios: empresa.limite_usuarios,
      logo_url: empresa.logo_url,
      modulos_permitidos: empresa.modulos_permitidos || []
    };
    
    localStorage.setItem("empresa", JSON.stringify(empresaContexto));
    setEmpresaAtual(empresaContexto);
    
    toast.success(`Acessando ambiente: ${empresa.nome_fantasia || empresa.razao_social}`);
    
    // Redirecionar para o dashboard
    navigate("/");
  };

  // Função para voltar ao ambiente Master (sem empresa selecionada)
  const voltarAmbienteMaster = () => {
    localStorage.removeItem("empresa");
    setEmpresaAtual(null);
    toast.info("Voltou ao ambiente Master");
    navigate("/");
  };

  const carregarEmpresas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/empresas?per_page=100`);
      const lista = response.data.empresas || [];
      setEmpresas(lista);
      
      // Calcular stats
      setStats({
        total: lista.length,
        ativas: lista.filter(e => e.status === "ATIVA").length,
        bloqueadas: lista.filter(e => e.status === "BLOQUEADA").length,
        inadimplentes: lista.filter(e => e.status === "INADIMPLENTE").length
      });
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (empresa = null) => {
    if (empresa) {
      setEditando(empresa);
      setFormData({
        razao_social: empresa.razao_social || "",
        nome_fantasia: empresa.nome_fantasia || "",
        cnpj: empresa.cnpj || "",
        email: empresa.email || "",
        telefone: empresa.telefone || "",
        endereco: empresa.endereco || "",
        cidade: empresa.cidade || "",
        estado: empresa.estado || "",
        cep: empresa.cep || "",
        plano: empresa.plano || "BASICO",
        status: empresa.status || "ATIVA",
        data_vencimento: empresa.data_vencimento || "",
        observacoes: empresa.observacoes || "",
        logo_url: empresa.logo_url || "",
        permissoes_empresa: empresa.permissoes_empresa || []
      });
      setLogoPreview(empresa.logo_url ? `${API}${empresa.logo_url}` : null);
    } else {
      setEditando(null);
      // Todas permissões por padrão para nova empresa
      const todasPermissoes = modulosSistema.flatMap(m => m.permissoes?.map(p => p.id) || []);
      setFormData({
        razao_social: "",
        nome_fantasia: "",
        cnpj: "",
        email: "",
        telefone: "",
        endereco: "",
        cidade: "",
        estado: "",
        cep: "",
        plano: "BASICO",
        status: "ATIVA",
        data_vencimento: "",
        observacoes: "",
        logo_url: "",
        permissoes_empresa: todasPermissoes
      });
      setLogoPreview(null);
    }
    setModulosExpandidos({});
    setActiveTab("dados");
    setModalOpen(true);
  };

  // Toggle expandir/colapsar módulo
  const toggleExpandirModulo = (moduloId) => {
    setModulosExpandidos(prev => ({
      ...prev,
      [moduloId]: !prev[moduloId]
    }));
  };

  // Upload de logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verificar tipo
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem");
      return;
    }
    
    // Preview local
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result);
    reader.readAsDataURL(file);
    
    // Se editando, fazer upload direto
    if (editando) {
      setUploadingLogo(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await axios.post(
          `${API}/api/empresas/${editando.id}/logo`,
          formDataUpload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        
        setFormData(prev => ({ ...prev, logo_url: response.data.logo_url }));
        toast.success("Logo atualizada com sucesso");
      } catch (error) {
        toast.error("Erro ao fazer upload da logo");
        setLogoPreview(null);
      } finally {
        setUploadingLogo(false);
      }
    }
  };
  
  // Remover logo
  const handleRemoveLogo = async () => {
    if (editando && formData.logo_url) {
      try {
        await axios.delete(`${API}/api/empresas/${editando.id}/logo`);
        toast.success("Logo removida");
      } catch (error) {
        toast.error("Erro ao remover logo");
        return;
      }
    }
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: "" }));
  };
  
  // Toggle permissão individual
  const togglePermissao = (permissaoId) => {
    setFormData(prev => {
      const permissoes = prev.permissoes_empresa || [];
      if (permissoes.includes(permissaoId)) {
        return { ...prev, permissoes_empresa: permissoes.filter(p => p !== permissaoId) };
      } else {
        return { ...prev, permissoes_empresa: [...permissoes, permissaoId] };
      }
    });
  };
  
  // Toggle todas permissões de um módulo
  const toggleModulo = (modulo) => {
    setFormData(prev => {
      const permissoes = prev.permissoes_empresa || [];
      const permissoesDoModulo = modulo.permissoes.map(p => p.id);
      const todasMarcadas = permissoesDoModulo.every(p => permissoes.includes(p));
      
      if (todasMarcadas) {
        // Desmarcar todas do módulo
        return { 
          ...prev, 
          permissoes_empresa: permissoes.filter(p => !permissoesDoModulo.includes(p))
        };
      } else {
        // Marcar todas do módulo
        const novasPermissoes = [...new Set([...permissoes, ...permissoesDoModulo])];
        return { ...prev, permissoes_empresa: novasPermissoes };
      }
    });
  };
  
  // Verificar se todas permissões do módulo estão marcadas
  const moduloCompleto = (modulo) => {
    const permissoes = formData.permissoes_empresa || [];
    return modulo.permissoes.every(p => permissoes.includes(p.id));
  };
  
  // Verificar se alguma permissão do módulo está marcada
  const moduloParcial = (modulo) => {
    const permissoes = formData.permissoes_empresa || [];
    const algumaMarcada = modulo.permissoes.some(p => permissoes.includes(p.id));
    const todasMarcadas = modulo.permissoes.every(p => permissoes.includes(p.id));
    return algumaMarcada && !todasMarcadas;
  };
  
  // Selecionar todas permissões
  const selecionarTodosModulos = () => {
    const todasPermissoes = modulosSistema.flatMap(m => m.permissoes.map(p => p.id));
    setFormData(prev => ({
      ...prev,
      permissoes_empresa: todasPermissoes
    }));
  };
  
  // Desmarcar todas permissões
  const desmarcarTodosModulos = () => {
    setFormData(prev => ({ ...prev, permissoes_empresa: [] }));
  };
  
  // Contar permissões selecionadas
  const contarPermissoes = () => {
    const total = modulosSistema.reduce((acc, m) => acc + m.permissoes.length, 0);
    const selecionadas = (formData.permissoes_empresa || []).length;
    return { total, selecionadas };
  };

  const salvarEmpresa = async () => {
    if (!formData.razao_social || !formData.cnpj || !formData.email) {
      toast.error("Razão Social, CNPJ e Email são obrigatórios");
      return;
    }

    setSaving(true);
    try {
      if (editando) {
        await axios.put(`${API}/api/empresas/${editando.id}`, formData);
        toast.success("Empresa atualizada com sucesso");
      } else {
        const response = await axios.post(`${API}/api/empresas`, formData);
        toast.success("Empresa criada com sucesso");
        
        // Se tiver logo para upload em nova empresa, fazer depois
        if (logoPreview && !formData.logo_url) {
          // Logo será tratado após criação
        }
      }
      
      setModalOpen(false);
      carregarEmpresas();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar empresa");
    } finally {
      setSaving(false);
    }
  };

  const alterarStatus = async (empresa, novoStatus) => {
    const acao = novoStatus === "ATIVA" ? "desbloquear" : "bloquear";
    if (!confirm(`Deseja ${acao} a empresa ${empresa.nome_fantasia || empresa.razao_social}?`)) return;
    
    try {
      await axios.patch(`${API}/api/empresas/${empresa.id}/status?status=${novoStatus}`);
      toast.success(`Empresa ${novoStatus === "ATIVA" ? "desbloqueada" : "bloqueada"}`);
      carregarEmpresas();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  const empresasFiltradas = empresas.filter(e => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!e.razao_social?.toLowerCase().includes(search) && 
          !e.nome_fantasia?.toLowerCase().includes(search) &&
          !e.cnpj?.includes(search)) {
        return false;
      }
    }
    if (filtroStatus !== "todos" && e.status !== filtroStatus) return false;
    if (filtroPlano !== "todos" && e.plano !== filtroPlano) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const configs = {
      ATIVA: { className: "bg-green-500", icon: CheckCircle, label: "Ativa" },
      BLOQUEADA: { className: "bg-red-500", icon: Lock, label: "Bloqueada" },
      SUSPENSA: { className: "bg-yellow-500", icon: AlertTriangle, label: "Suspensa" },
      INADIMPLENTE: { className: "bg-orange-500", icon: XCircle, label: "Inadimplente" }
    };
    const config = configs[status] || configs.BLOQUEADA;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} text-white gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPlanoBadge = (plano) => {
    const configs = {
      BASICO: { className: "bg-slate-600", label: "Básico" },
      PROFISSIONAL: { className: "bg-blue-600", label: "Profissional" },
      ENTERPRISE: { className: "bg-purple-600", label: "Enterprise" }
    };
    const config = configs[plano] || configs.BASICO;
    return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Gestão de Empresas
          </h1>
          <p className="text-gray-500">Gerencie as empresas cadastradas no sistema</p>
        </div>
        
        <div className="flex gap-2">
          {empresaAtual && (
            <Button variant="outline" onClick={voltarAmbienteMaster} className="gap-2">
              <ArrowRight className="h-4 w-4 rotate-180" />
              Voltar ao Master
            </Button>
          )}
          <Button onClick={() => abrirModal()} className="gap-2">
            <Plus className="h-4 w-4" />
            Nova Empresa
          </Button>
        </div>
      </div>

      {/* Empresa Atual */}
      {empresaAtual && (
        <Card className="border-2 border-blue-500 bg-blue-50">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Ambiente Atual</p>
                  <p className="text-lg font-bold text-blue-800">
                    {empresaAtual.nome_fantasia || empresaAtual.razao_social}
                  </p>
                </div>
              </div>
              <Badge className="bg-blue-500">
                {empresaAtual.cnpj}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ativas</p>
                <p className="text-2xl font-bold text-green-600">{stats.ativas}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Bloqueadas</p>
                <p className="text-2xl font-bold text-red-600">{stats.bloqueadas}</p>
              </div>
              <Lock className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inadimplentes</p>
                <p className="text-2xl font-bold text-orange-600">{stats.inadimplentes}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome ou CNPJ..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos Status</SelectItem>
                <SelectItem value="ATIVA">Ativa</SelectItem>
                <SelectItem value="BLOQUEADA">Bloqueada</SelectItem>
                <SelectItem value="SUSPENSA">Suspensa</SelectItem>
                <SelectItem value="INADIMPLENTE">Inadimplente</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filtroPlano} onValueChange={setFiltroPlano}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Plano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos Planos</SelectItem>
                <SelectItem value="BASICO">Básico</SelectItem>
                <SelectItem value="PROFISSIONAL">Profissional</SelectItem>
                <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={carregarEmpresas}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>CNPJ</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Usuários</TableHead>
                <TableHead>Mensalidade</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {empresasFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    Nenhuma empresa encontrada
                  </TableCell>
                </TableRow>
              ) : (
                empresasFiltradas.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{empresa.nome_fantasia || empresa.razao_social}</p>
                        <p className="text-xs text-gray-500">{empresa.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{empresa.cnpj}</TableCell>
                    <TableCell>{getPlanoBadge(empresa.plano)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span>{empresa.usuarios_ativos || 0}/{empresa.limite_usuarios}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-green-600">
                      {formatCurrency(empresa.valor_mensalidade)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {empresa.data_vencimento || "---"}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(empresa.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Botão Acessar Empresa */}
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700 gap-1"
                          onClick={() => acessarEmpresa(empresa)}
                          disabled={empresa.status !== "ATIVA"}
                          title="Acessar ambiente da empresa"
                        >
                          <LogIn className="h-4 w-4" />
                          Acessar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => abrirModal(empresa)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {empresa.status === "ATIVA" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => alterarStatus(empresa, "BLOQUEADA")}
                            title="Bloquear"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600"
                            onClick={() => alterarStatus(empresa, "ATIVA")}
                            title="Desbloquear"
                          >
                            <Unlock className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editando ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editando ? "Editar Empresa" : "Nova Empresa"}
            </DialogTitle>
            <DialogDescription>
              {editando ? "Atualize os dados da empresa" : "Cadastre uma nova empresa"}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="logo">Logo</TabsTrigger>
              <TabsTrigger value="modulos">Módulos</TabsTrigger>
            </TabsList>
            
            <ScrollArea className="flex-1 pr-4">
              {/* Aba Dados */}
              <TabsContent value="dados" className="space-y-4 mt-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Razão Social *</Label>
                    <Input
                      value={formData.razao_social}
                      onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                      placeholder="Razão Social da empresa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Fantasia</Label>
                    <Input
                      value={formData.nome_fantasia}
                      onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                      placeholder="Nome Fantasia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CNPJ *</Label>
                    <Input
                      value={formData.cnpj}
                      onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contato@empresa.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      placeholder="(00) 0000-0000"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Endereço</Label>
                    <Input
                      value={formData.endereco}
                      onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                      placeholder="Rua, número, bairro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      placeholder="Cidade"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Input
                        value={formData.estado}
                        onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                        placeholder="UF"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CEP</Label>
                      <Input
                        value={formData.cep}
                        onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                        placeholder="00000-000"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Plano e Configurações
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Plano *</Label>
                      <Select 
                        value={formData.plano} 
                        onValueChange={(v) => setFormData({ ...formData, plano: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o plano" />
                        </SelectTrigger>
                        <SelectContent>
                          {PLANOS.map((plano) => (
                            <SelectItem key={plano.value} value={plano.value}>
                              {plano.label} - {plano.usuarios} usuários - {formatCurrency(plano.valor)}/mês
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={formData.status} 
                        onValueChange={(v) => setFormData({ ...formData, status: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ATIVA">Ativa</SelectItem>
                          <SelectItem value="BLOQUEADA">Bloqueada</SelectItem>
                          <SelectItem value="SUSPENSA">Suspensa</SelectItem>
                          <SelectItem value="INADIMPLENTE">Inadimplente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Data de Vencimento</Label>
                      <Input
                        type="date"
                        value={formData.data_vencimento}
                        onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">
                        Assinatura será bloqueada automaticamente após esta data
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Observações sobre a empresa..."
                    rows={3}
                  />
                </div>
              </TabsContent>
              
              {/* Aba Logo */}
              <TabsContent value="logo" className="space-y-4 mt-4">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo da empresa" 
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Image className="h-12 w-12 mx-auto mb-2" />
                        <p className="text-sm">Nenhuma logo</p>
                      </div>
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {logoPreview ? "Trocar Logo" : "Carregar Logo"}
                    </Button>
                    {logoPreview && (
                      <Button 
                        variant="destructive" 
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remover
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 text-center">
                    Formatos aceitos: PNG, JPG, WEBP, SVG<br />
                    Tamanho recomendado: 200x200 pixels
                  </p>
                </div>
              </TabsContent>
              
              {/* Aba Módulos */}
              <TabsContent value="modulos" className="space-y-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Módulos e Permissões do Pacote
                    </h4>
                    <p className="text-sm text-gray-500">
                      Defina quais módulos e permissões a empresa terá acesso
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selecionarTodosModulos}>
                      Marcar Todos
                    </Button>
                    <Button variant="outline" size="sm" onClick={desmarcarTodosModulos}>
                      Desmarcar Todos
                    </Button>
                  </div>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {modulosSistema.map((modulo) => {
                    const IconComponent = ICONE_MAP[modulo.icone] || Settings;
                    const isExpanded = modulosExpandidos[modulo.id];
                    const isCompleto = moduloCompleto(modulo);
                    const isParcial = moduloParcial(modulo);
                    const permissoesSelecionadas = modulo.permissoes?.filter(p => (formData.permissoes_empresa || []).includes(p.id)).length || 0;
                    const totalPermissoes = modulo.permissoes?.length || 0;
                    
                    return (
                      <Card 
                        key={modulo.id} 
                        className={`
                          overflow-hidden transition-all cursor-pointer
                          ${isCompleto 
                            ? "border-primary bg-primary/5" 
                            : isParcial
                              ? "border-yellow-400 bg-yellow-50"
                              : "hover:border-gray-300"
                          }
                        `}
                      >
                        {/* Cabeçalho do módulo */}
                        <div 
                          className="p-3 flex items-center gap-3"
                          onClick={() => toggleExpandirModulo(modulo.id)}
                        >
                          <Checkbox 
                            checked={isCompleto}
                            className={isParcial && !isCompleto ? "data-[state=checked]:bg-yellow-500" : ""}
                            onCheckedChange={() => toggleModulo(modulo)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className={`
                            p-2 rounded-lg
                            ${isCompleto ? "bg-primary/10" : isParcial ? "bg-yellow-100" : "bg-gray-100"}
                          `}>
                            <IconComponent className={`h-5 w-5 ${isCompleto ? "text-primary" : isParcial ? "text-yellow-600" : "text-gray-500"}`} />
                          </div>
                          <div className="flex-1">
                            <span className={`font-medium text-sm ${isCompleto ? "text-primary" : "text-gray-700"}`}>
                              {modulo.nome}
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={isCompleto ? "default" : isParcial ? "warning" : "secondary"} className="text-xs">
                                {permissoesSelecionadas}/{totalPermissoes}
                              </Badge>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        
                        {/* Permissões do módulo (expandido) */}
                        {isExpanded && modulo.permissoes && (
                          <div className="px-3 pb-3 border-t pt-3 space-y-2 bg-white">
                            {modulo.permissoes.map((permissao) => {
                              const isChecked = (formData.permissoes_empresa || []).includes(permissao.id);
                              return (
                                <div
                                  key={permissao.id}
                                  className={`
                                    flex items-center gap-2 p-2 rounded cursor-pointer transition-all
                                    ${isChecked 
                                      ? "bg-primary/5" 
                                      : "hover:bg-gray-50"
                                    }
                                  `}
                                  onClick={() => togglePermissao(permissao.id)}
                                >
                                  <Checkbox 
                                    checked={isChecked}
                                    onCheckedChange={() => togglePermissao(permissao.id)}
                                  />
                                  <span className={`text-xs ${isChecked ? "text-primary font-medium" : "text-gray-600"}`}>
                                    {permissao.nome}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {contarPermissoes().selecionadas} permissões selecionadas
                    </Badge>
                    <span className="text-sm text-gray-500">
                      de {contarPermissoes().total} disponíveis
                    </span>
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarEmpresa} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
